import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown, X, ZoomIn } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const clientLogos = [
  { src: '/partners/research.png', alt: 'Research Client', h: 'h-12 sm:h-16', grayscale: true, w: 96, h_n: 64 },
  { src: 'https://i.ibb.co/jvbs9tXs/New-Project-1.png', alt: 'Client Logo', h: 'h-12 sm:h-16', grayscale: true, w: 96, h_n: 64 },
  { src: 'https://iili.io/qivjCX9.md.png', alt: 'Partner Logo', h: 'h-8 sm:h-10', grayscale: false, w: 64, h_n: 40 },
  { src: 'https://i.ibb.co/Fqk0D19R/New-Project.png', alt: 'Client Logo', h: 'h-12 sm:h-16', grayscale: true, w: 96, h_n: 64 },
  { src: 'https://i.ibb.co/FL2Z5qcp/behind-the-reels-logo-1-1.png', alt: 'Client Logo', h: 'h-12 sm:h-16', grayscale: true, w: 96, h_n: 64 },
]

const Clients = ({ duration = 20 }) => {
  const { t } = useTranslation()
  const trackRef = useRef(null)
  const tweenRef = useRef(null)
  const stRef = useRef(null)
  const imagesLoaded = useRef(false)
  const [ready, setReady] = useState(false)
  const loadedCount = useRef(0)

  const handleImageLoad = () => {
    loadedCount.current++
    if (loadedCount.current >= clientLogos.length && !imagesLoaded.current) {
      imagesLoaded.current = true
      setReady(true)
    }
  }

  const initMarquee = useCallback(() => {
    const track = trackRef.current
    if (!track || !imagesLoaded.current) return

    tweenRef.current?.kill()
    stRef.current?.kill()
    gsap.set(track, { x: 0 })

    const container = track.parentElement
    const containerWidth = container.offsetWidth
    const children = Array.from(track.children)
    const firstSet = children.slice(0, clientLogos.length)

    children.slice(clientLogos.length).forEach((el) => el.remove())

    const setWidth = track.scrollWidth
    if (setWidth === 0) return

    const copiesNeeded = Math.ceil(containerWidth / setWidth) + 1

    for (let i = 1; i < copiesNeeded; i++) {
      firstSet.forEach((child) => {
        track.appendChild(child.cloneNode(true))
      })
    }

    requestAnimationFrame(() => {
      const oneSetWidth = track.scrollWidth / copiesNeeded

      tweenRef.current = gsap.fromTo(track, { x: 0 }, {
        x: -oneSetWidth,
        duration,
        repeat: -1,
        ease: 'none',
      })

      stRef.current = ScrollTrigger.create({
        trigger: container,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => tweenRef.current?.play(),
        onLeave: () => tweenRef.current?.pause(),
        onEnterBack: () => tweenRef.current?.play(),
        onLeaveBack: () => tweenRef.current?.pause(),
      })
    })
  }, [duration])

  useEffect(() => {
    if (ready) {
      initMarquee()
    }
  }, [ready, initMarquee])

  useEffect(() => {
    const handleResize = () => {
      if (imagesLoaded.current) initMarquee()
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      tweenRef.current?.kill()
      stRef.current?.kill()
    }
  }, [initMarquee])

  return (
    <section className="py-6 sm:py-8 bg-[#0a0a0f] relative overflow-hidden" dir="ltr">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      
      <div className="container-custom relative z-10">
        <p className="text-center text-gray-500 text-xs sm:text-sm mb-6 tracking-widest font-medium uppercase">
          {t('clients.heading', 'Our Clients')}
        </p>
        <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]">
          <div
            ref={trackRef}
            dir="ltr"
            className="flex items-center gap-10 sm:gap-14 w-max"
          >
            {clientLogos.map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={logo.alt}
                width={logo.w}
                height={logo.h_n}
                className={`${logo.h} w-auto object-contain opacity-70 hover:opacity-100 transition-all duration-300 shrink-0 ${logo.grayscale ? 'grayscale hover:grayscale-0' : ''}`}
                onLoad={handleImageLoad}
                onError={handleImageLoad}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

const Lightbox = ({ image, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'auto'
    }
  }, [onClose])

  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      
      <motion.div 
        className="relative max-w-6xl max-h-[90vh]"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={image.src} 
          alt={image.alt} 
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
        />
      </motion.div>
    </motion.div>
  )
}

const WorkflowShowcase = () => {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  
  const workflows = [
    { src: 'https://i.ibb.co/zHQL5Jhf/1.png', alt: 'Workflow 1' },
    { src: 'https://i.ibb.co/Zz3TRBhf/2.png', alt: 'Workflow 2' },
    { src: 'https://i.ibb.co/SX8RTSc7/3.png', alt: 'Workflow 3' },
    { src: 'https://i.ibb.co/zWRVD1CK/4.png', alt: 'Workflow 4' },
    { src: 'https://i.ibb.co/JFH9Z4sL/5.png', alt: 'Workflow 5' },
  ]

  const displayedWorkflows = showAll ? workflows : workflows.slice(0, 3)
  const hiddenWorkflows = workflows.slice(3)
  const hasMoreWorkflows = workflows.length > 3

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.94 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.08,
        duration: 0.45,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
    exit: { opacity: 0, y: -20, scale: 0.94, transition: { duration: 0.25, ease: 'easeIn' } },
  }

  return (
    <section className="py-16 sm:py-20 bg-[#0a0a0f] relative overflow-hidden">
      <AnimatePresence>
        {selectedImage && (
          <Lightbox image={selectedImage} onClose={() => setSelectedImage(null)} />
        )}
      </AnimatePresence>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#1a1a2e]/20 to-[#0a0a0f]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-500/5 rounded-full blur-[150px]"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
            {t('workflows.headingBefore', 'Our')} <span className="text-purple-400">{t('workflows.headingHighlight', 'Automation')}</span> {t('workflows.headingAfter', 'Samples')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('workflows.description', 'How we transform manual processes into automated workflows')}
          </p>
        </div>

        {/* Gallery */}
        <motion.div layout className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <AnimatePresence>
              {displayedWorkflows.map((workflow, index) => (
                <motion.div 
                  key={workflow.src}
                  layout
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="group relative cursor-pointer"
                  onClick={() => setSelectedImage(workflow)}
                >
                  {/* Glow effect */}
                  <div className="absolute -inset-2 bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                  
                  {/* Image container */}
                  <div className="relative overflow-hidden rounded-xl bg-white/5 border border-white/10 group-hover:border-purple-500/30 transition-all duration-500">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={workflow.src} 
                        alt={workflow.alt} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                      />
                    </div>
                    
                    {/* Hover overlay with zoom icon */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                        <ZoomIn className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Show More Button */}
          {hasMoreWorkflows && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => setShowAll(!showAll)}
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300 backdrop-blur-sm active:scale-[0.98]"
              >
                <span className="text-white font-medium">
                  {showAll ? t('workflows.showLess', 'Show Less') : t('workflows.showMore', { count: workflows.length - 3, defaultValue: `View ${workflows.length - 3} More Workflows` })}
                </span>
                <motion.div
                  animate={{ rotate: showAll ? 180 : 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  className="text-gray-400 group-hover:text-purple-400"
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  )
}

export { Clients, WorkflowShowcase, SoftwareShowcase }

const SoftwareShowcase = () => {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  
  const software = [
    { src: 'https://i.ibb.co/fV33yHg8/1.png', alt: 'Software Screenshot', title: 'Software Screenshot' },
    { src: 'https://i.ibb.co/wZxgNf5C/Screenshot-2026-05-02-151903.png', alt: 'CRM System', title: 'CRM System' },
    { src: 'https://i.ibb.co/14JjpNK/2.png', alt: 'Software Screenshot', title: 'Software Screenshot' },
    { src: 'https://i.ibb.co/d4y1sRfS/Screenshot-2026-05-02-151937.png', alt: 'Dashboard', title: 'Dashboard' },
  ]

  const INITIAL_COUNT = 3
  const displayedSoftware = showAll ? software : software.slice(0, INITIAL_COUNT)
  const hasMoreSoftware = software.length > INITIAL_COUNT

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.94 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.08,
        duration: 0.45,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
    exit: { opacity: 0, y: -20, scale: 0.94, transition: { duration: 0.25, ease: 'easeIn' } },
  }

  return (
    <section className="py-16 sm:py-20 bg-[#0a0a0f] relative overflow-hidden">
      <AnimatePresence>
        {selectedImage && (
          <Lightbox image={selectedImage} onClose={() => setSelectedImage(null)} />
        )}
      </AnimatePresence>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#1a1a2e]/20 to-[#0a0a0f]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-500/5 rounded-full blur-[150px]"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
            {t('software.headingBefore', 'Software We')} <span className="text-purple-400">{t('software.headingHighlight', 'Build')}</span>
          </h2>
          <p className="text-gray-400 text-lg">
            {t('software.description', 'Custom software solutions for enterprise companies')}
          </p>
        </div>

        {/* Gallery */}
        <motion.div layout className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <AnimatePresence>
              {displayedSoftware.map((item, index) => (
                <motion.div 
                  key={item.src}
                  layout
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="group relative cursor-pointer"
                  onClick={() => setSelectedImage(item)}
                >
                  {/* Glow effect */}
                  <div className="absolute -inset-2 bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                  
                  {/* Image container */}
                  <div className="relative overflow-hidden rounded-xl bg-white/5 border border-white/10 group-hover:border-purple-500/30 transition-all duration-500">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={item.src} 
                        alt={item.alt} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                      />
                    </div>
                    
                    {/* Hover overlay with zoom icon */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                        <ZoomIn className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Show More Button */}
          {hasMoreSoftware && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => setShowAll(!showAll)}
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300 backdrop-blur-sm active:scale-[0.98]"
              >
                <span className="text-white font-medium">
                  {showAll ? t('software.showLess', 'Show Less') : t('software.showMore', 'View More Projects')}
                </span>
                <motion.div
                  animate={{ rotate: showAll ? 180 : 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  className="text-gray-400 group-hover:text-purple-400"
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  )
}

export default Clients